import { zipSync, strToU8 } from 'fflate'

/**
 * Dummy files for sample records, so a download on a sample row hands back
 * something real to open instead of doing nothing. Every file says "SAMPLE"
 * on it — nothing here is anyone's actual ledger.
 */

const enc = new TextEncoder()
const esc = (s) => String(s).replace(/[\\()]/g, (c) => `\\${c}`)

/** A one-page PDF with a title and a few lines of text. */
function samplePdfBytes(title, lines = []) {
  const text = [
    'BT /F1 18 Tf 50 790 Td',
    `(${esc(title)}) Tj`,
    '/F1 10 Tf 0 -22 Td',
    '(SAMPLE DATA - not a real ledger) Tj',
    ...lines.flatMap((l) => ['0 -16 Td', `(${esc(l)}) Tj`]),
    'ET',
  ].join('\n')
  const objs = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    `<< /Length ${enc.encode(text).length} >>\nstream\n${text}\nendstream`,
  ]
  let out = '%PDF-1.4\n'
  const offsets = []
  objs.forEach((o, i) => {
    offsets.push(enc.encode(out).length)
    out += `${i + 1} 0 obj\n${o}\nendobj\n`
  })
  const xref = enc.encode(out).length
  out += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n`
  out += offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')
  out += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`
  return enc.encode(out)
}

const xmlEsc = (s) => String(s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c])
const colName = (i) => String.fromCharCode(65 + i)

/** A one-sheet .xlsx from rows of cells (strings or numbers). */
function sampleXlsxBytes(sheetName, rows) {
  const sheetRows = rows
    .map((r, ri) => `<row r="${ri + 1}">${r
      .map((v, ci) => {
        const ref = `${colName(ci)}${ri + 1}`
        return typeof v === 'number'
          ? `<c r="${ref}"><v>${v}</v></c>`
          : `<c r="${ref}" t="inlineStr"><is><t>${xmlEsc(v ?? '')}</t></is></c>`
      })
      .join('')}</row>`)
    .join('')
  const files = {
    '[Content_Types].xml': `<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`,
    '_rels/.rels': `<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    'xl/workbook.xml': `<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${xmlEsc(sheetName)}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    'xl/_rels/workbook.xml.rels': `<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`,
    'xl/worksheets/sheet1.xml': `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetRows}</sheetData></worksheet>`,
  }
  return zipSync(Object.fromEntries(Object.entries(files).map(([k, v]) => [k, strToU8(v)])))
}

/** A sample client's statement. */
export const sampleStatementBytes = (label = 'Sample client') =>
  samplePdfBytes(`Ledger statement - ${label}`, [
    'Date        Particulars                 Debit        Credit',
    '01-04-2026  Opening balance             1,20,000.00',
    '12-05-2026  Sales JWS/26-27/0452        2,40,000.00',
    '30-06-2026  Receipt HDFC UTR 0983                    1,50,000.00',
    '31-07-2026  Closing balance             2,10,000.00',
  ])

/** A sample reconciliation workbook. */
export const sampleReconciliationBytes = () =>
  sampleXlsxBytes('Reconciliation', [
    ['SAMPLE DATA - not a real reconciliation'],
    [],
    ['Date', 'Reference', 'Our side', 'Their side', 'Difference', 'Who fixes'],
    ['2026-05-12', 'JWS/26-27/0452', 240000, 0, 240000, 'Client'],
    ['2026-06-30', 'UTR HDFCH01249269838', 150000, 150000, 0, '—'],
    ['2026-07-18', 'TDS Q1', 0, 18450, -18450, 'Us'],
  ])

/** Save bytes as a file in the browser. */
export function saveBytes(bytes, fileName, type = 'application/octet-stream') {
  const url = URL.createObjectURL(new Blob([bytes], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Tell the person a sample action was simulated — see `Toaster` in App. */
export function sampleToast(message = 'Sample data — simulated. Nothing was sent or saved.') {
  window.dispatchEvent(new CustomEvent('lereco:toast', { detail: { kind: 'info', message } }))
}
